import React from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare } from '@fortawesome/free-solid-svg-icons';
import { faMinusSquare } from '@fortawesome/free-solid-svg-icons';
import { faSquare } from '@fortawesome/free-regular-svg-icons';

import { renderableType } from '../nimbus/proptypes/prop-types';
import { buildClassNames, buildClassNameModifiers } from '../nimbus/util/utils';
import "./TCCheckbox.scss";

const blockBaseClassName = 'checkbox';
const labelClassName = 'checkbox__label';
const labelContentClassName = 'checkbox__labelContent';
const inputClassName = 'checkbox__input';
const indicatorClassName = 'checkbox__indicator';
const textClassName = 'checkbox__text';
const uncheckedIconClassName = 'checkbox__uncheckedIcon';
const checkedIconClassName = 'checkbox__checkedIcon';

export const TCCheckbox = ({
                    children,
                    className,
                    checked,
                    disabled,
                    id,
                    iconChecked,
                    iconIndeterminate,
                    iconUnchecked,
                    indeterminate,
                    inline,
                    onChange,
                    onClick,
                    title,
                    ...other
                  }) => {
  const blockClassNames = buildClassNames(
    className,
    buildClassNameModifiers(blockBaseClassName, { checked }, { disabled }, { inline }),
    blockBaseClassName,
  );
  const labelClassNames = buildClassNames(
    buildClassNameModifiers(labelClassName, { checked }, { disabled }, { inline }),
    labelClassName,
  );
  const indicatorClassNames = buildClassNames(
    buildClassNameModifiers(indicatorClassName, { checked }, { disabled }),
    indicatorClassName,
  );

  return (
    <div className={blockClassNames} {...other}>
      <label htmlFor={id} className={labelClassNames} title={title}>
        <input
          id={id}
          className={inputClassName}
          type='checkbox'
          checked={checked}
          disabled={disabled}
          onClick={onClick}
          onChange={onChange}
          {...other}
        />
        <div className={labelContentClassName}>
          <div className={indicatorClassNames}>
            <CSSTransition
              classNames={uncheckedIconClassName}
              in={!checked}
              timeout={150}
              mountOnEnter
              unmountOnExit
            >
              {() => <div className={uncheckedIconClassName}>{iconUnchecked}</div>}
            </CSSTransition>
            <CSSTransition
              classNames={checkedIconClassName}
              in={checked}
              timeout={150}
              mountOnEnter
              unmountOnExit
            >
              {() => (indeterminate
                  ? <div className={checkedIconClassName}>{iconIndeterminate}</div>
                  : <div className={checkedIconClassName}>{iconChecked}</div>
              )}
            </CSSTransition>
          </div>
          <div className={textClassName}>{children}</div>
        </div>
      </label>
    </div>
  );
};

TCCheckbox.propTypes = {
  /** @ignore */
  children: renderableType,
  /** @ignore */
  className: PropTypes.string,
  /** Indicates whether the checkbox is checked */
  checked: PropTypes.bool,
  /** Indicates whether the checkbox is disabled */
  disabled: PropTypes.bool,
  /** The id assigned to the checkbox input */
  id: PropTypes.string,
  /** Icon rendered when the checkbox is checked */
  iconChecked: renderableType,
  /** Icon rendered when the checkbox is in an indeterminate state */
  iconIndeterminate: renderableType,
  /** Icon rendered when the checkbox is unchecked */
  iconUnchecked: renderableType,
  /** Indicates that the checkbox is in an indeterminate state */
  indeterminate: PropTypes.bool,
  /** Indicates that the checkbox is rendered inline */
  inline: PropTypes.bool,
  /** Function executed when the checkbox value changes */
  onChange: PropTypes.func,
  /** Function executed when the checkbox is clicked */
  onClick: PropTypes.func,
  /** Text displayed on hover */
  title: PropTypes.string,
};

TCCheckbox.defaultProps = {
  checked: false,
  iconChecked: <FontAwesomeIcon fixedWidth icon={faCheckSquare} />,
  iconIndeterminate: <FontAwesomeIcon fixedWidth icon={faMinusSquare} />,
  iconUnchecked: <FontAwesomeIcon fixedWidth icon={faSquare} />,
  inline: false,
  disabled: false,
};
